import Phaser from 'phaser'
import { paintBoardTerrainSample } from '../visual/boardSample'
import { paintEnemyVisual, paintProjectileVisual, paintTowerVisual } from '../visual/entityDraw'
import { ENEMY_VISUALS } from '../visual/enemies'
import { PROJECTILE_VISUALS } from '../visual/projectiles'
import { THEME } from '../visual/theme'
import { TOWER_VISUALS } from '../visual/towers'

const CELL = 36
const PAD = 40

const TOWER_GALLERY_ROWS: Array<{ id: keyof typeof TOWER_VISUALS; label: string }> = [
  { id: 'fire', label: 'FIRE — directional emitter △' },
  { id: 'frost', label: 'FROST — crystal hex' },
  { id: 'cannon', label: 'CANNON — mass block + bore' },
  { id: 'poison', label: 'POISON — diamond dart' },
  { id: 'aa', label: 'AA — twin prongs / tracker' },
  { id: 'mage', label: 'MAGE — fracture prism' }
]

const ENEMY_GALLERY: Array<{
  id: keyof typeof ENEMY_VISUALS
  label: string
  air?: boolean
  elite?: boolean
  hasShield?: boolean
}> = [
  { id: 'normal', label: 'NORMAL' },
  { id: 'fast', label: 'FAST' },
  { id: 'tank', label: 'TANK' },
  { id: 'regen', label: 'REGEN' },
  { id: 'shield', label: 'SHIELD', hasShield: true },
  { id: 'guardian', label: 'GUARD' },
  { id: 'phase', label: 'PHASE' },
  { id: 'crystal', label: 'CRYSTAL' },
  { id: 'flyer', label: 'FLYER', air: true },
  { id: 'boss', label: 'BOSS' },
  { id: 'air_boss', label: 'AIR_BOSS', air: true },
  { id: 'normal', label: 'ELITE', elite: true }
]

/**
 * DEV-only gallery — paints production visual language samples.
 * Not used in campaign match flow.
 */
export class VisualGalleryScene extends Phaser.Scene {
  constructor() {
    super({ key: 'VisualGalleryScene' })
  }

  create(): void {
    this.cameras.main.setBackgroundColor(THEME.background)
    this.cameras.main.setScroll(0, 0)

    const root = this.add.container(0, 0)
    const g = this.add.graphics()
    root.add(g)

    let y = PAD

    this.add
      .text(PAD, y - 14, 'Terrain / path sample', {
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        fontSize: '13px',
        color: '#94a3b8'
      })
      .setOrigin(0, 1)

    paintBoardTerrainSample(g, PAD, y, CELL)
    y += CELL * 4 + 40

    for (const row of TOWER_GALLERY_ROWS) {
      const visual = TOWER_VISUALS[row.id]!
      this.add
        .text(PAD, y - 14, `${row.label} · LV1→LV4`, {
          fontFamily: 'Segoe UI, system-ui, sans-serif',
          fontSize: '13px',
          color: '#94a3b8'
        })
        .setOrigin(0, 1)

      for (let lv = 1; lv <= 4; lv += 1) {
        const tg = this.add.graphics()
        const x = PAD + 28 + (lv - 1) * (CELL * 2.5)
        paintTowerVisual(tg, visual, lv, CELL * 1.85, CELL * 1.85, CELL, {
          selected: lv === 4
        })
        tg.setPosition(x, y + CELL)
        root.add(tg)
        this.add
          .text(x, y + CELL * 2.05, `LV${lv}${lv === 4 ? ' ·sel' : ''}`, {
            fontFamily: 'Segoe UI, system-ui, sans-serif',
            fontSize: '10px',
            color: '#64748b'
          })
          .setOrigin(0.5, 0)
      }
      y += CELL * 3.2 + 28
    }

    this.add
      .text(PAD, y - 14, 'Enemies — 11 families + ELITE sample', {
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        fontSize: '13px',
        color: '#94a3b8'
      })
      .setOrigin(0, 1)

    const cols = 4
    const colW = CELL * 2.35
    const rowH = CELL * 2.55
    ENEMY_GALLERY.forEach((e, i) => {
      const col = i % cols
      const row = Math.floor(i / cols)
      const eg = this.add.graphics()
      const r = CELL * (e.id === 'boss' || e.id === 'air_boss' ? 0.42 : 0.38)
      paintEnemyVisual(eg, ENEMY_VISUALS[e.id]!, r, CELL, {
        elite: e.elite,
        hasShield: e.hasShield
      })
      const x = PAD + 28 + col * colW
      const ey = y + CELL * 0.95 + row * rowH
      if (e.air) {
        const shadow = this.add.graphics()
        shadow.fillStyle(0x000000, 0.4)
        shadow.fillEllipse(0, 14, CELL * 0.55, CELL * 0.18)
        shadow.setPosition(x, ey + 8)
        root.add(shadow)
        eg.setPosition(x, ey - 10)
      } else {
        eg.setPosition(x, ey)
      }
      root.add(eg)
      this.add
        .text(x, ey + CELL * 0.95, e.label, {
          fontFamily: 'Segoe UI, system-ui, sans-serif',
          fontSize: '9px',
          color: '#64748b'
        })
        .setOrigin(0.5, 0)
    })
    y += rowH * Math.ceil(ENEMY_GALLERY.length / cols) + 36

    this.add
      .text(PAD, y - 14, 'FIRE projectile (flare) + Burn mark', {
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        fontSize: '13px',
        color: '#94a3b8'
      })
      .setOrigin(0, 1)

    const pg = this.add.graphics()
    const proj = PROJECTILE_VISUALS.fire!
    paintProjectileVisual(pg, proj, CELL * proj.radiusFactor * 2.4, CELL)
    pg.setPosition(PAD + 36, y + CELL * 0.75)
    pg.setRotation(-0.35)
    root.add(pg)

    const burn = this.add.graphics()
    burn.lineStyle(2.5, THEME.burnMark, 0.95)
    burn.strokeCircle(0, 0, CELL * 0.38)
    burn.fillStyle(THEME.burnMark, 0.28)
    burn.fillCircle(0, 0, CELL * 0.22)
    burn.setPosition(PAD + 120, y + CELL * 0.75)
    root.add(burn)

    this.add
      .text(PAD + 150, y + CELL * 0.75, 'Burn', {
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        fontSize: '12px',
        color: '#94a3b8'
      })
      .setOrigin(0, 0.5)

    if (import.meta.env.DEV) {
      ;(globalThis as { __shapesTdGallery?: string }).__shapesTdGallery = 'ready'
    }
  }
}
